<template>
    <div>
        <!-- Boîte à outils et filtres au-dessus de l'arbre -->
        <div class="card border-0 shadow-sm mb-4" style="border-radius: 12px;">
            <div class="card-header bg-white border-0 pt-4 px-4 pb-0 d-flex justify-content-between align-items-center">
                <div>
                    <h2 class="h5 font-weight-bold mb-1 text-dark">
                        <i class="far fa-sitemap mr-2 text-primary"></i> Arborescence des familles d'articles
                    </h2>
                    <p class="text-muted small mb-0">Organisez vos articles par catégories et sous-catégories. Glissez-déposez pour réorganiser.</p>
                </div>
                <!-- Actions d'arbre -->
                <div>
                    <!-- Bascule vue colonnes / arborescente désactivée : on garde uniquement la vue en colonnes
                    <div class="btn-group mr-2 view-mode-switch" role="group">
                        <button type="button" class="ix-btn gray" :class="{ active: viewMode === 'columns' }" @click="setViewMode('columns')" title="Vue en colonnes">
                            <i class="far fa-columns"></i>
                        </button>
                        <button type="button" class="ix-btn gray" :class="{ active: viewMode === 'tree' }" @click="setViewMode('tree')" title="Vue arborescente">
                            <i class="far fa-stream"></i>
                        </button>
                    </div>
                    -->
                    <button v-if="viewMode === 'tree'" class="ix-btn gray mr-2" @click="toggleAllNodes" :title="allExpanded ? 'Tout réduire' : 'Tout développer'">
                        <i :class="allExpanded ? 'far fa-compress-alt' : 'far fa-expand-arrows-alt'"></i>
                        <span class="d-none d-sm-inline ml-1">{{ allExpanded ? 'Tout réduire' : 'Tout développer' }}</span>
                    </button>
                    <a v-if="canCreate" href="#" class="ix-btn gray mr-2" @click.prevent="openImportModal">
                        <i class="far fa-file-import"></i>
                        <span class="d-none d-sm-inline ml-1">Importer</span>
                    </a>
                    <button v-if="canCreate" class="ix-btn blue" @click="openCreateModal(null)">
                        <i class="far fa-plus"></i> <span class="d-none d-sm-inline ml-1">Nouvelle famille</span>
                    </button>
                </div>
            </div>
            
            <div class="card-body px-4 py-3">
                <!-- Barre de recherche -->
                <div class="input-group mb-3">
                    <div class="input-group-prepend">
                        <span class="input-group-text bg-white border-right-0" style="border-top-left-radius: 8px; border-bottom-left-radius: 8px;">
                            <i class="far fa-search text-muted"></i>
                        </span>
                    </div>
                    <input type="text" 
                           class="form-control border-left-0" 
                           placeholder="Rechercher une famille ou sous-famille..." 
                           v-model="searchQuery"
                           style="border-top-right-radius: 8px; border-bottom-right-radius: 8px;">
                </div>

                <!-- Loader de chargement -->
                <div v-if="loading" class="text-center py-5">
                    <i class="far fa-circle-notch fa-spin fa-2x text-primary mb-2"></i>
                    <p class="text-muted mb-0">Chargement de l'arborescence des familles...</p>
                </div>

                <!-- Message d'erreur -->
                <div v-else-if="error" class="alert alert-danger">
                    <i class="far fa-exclamation-triangle mr-2"></i> {{ error }}
                    <button class="btn btn-sm btn-outline-danger float-right" @click="fetchFamilies" style="margin-top: -3px;">Réessayer</button>
                </div>

                <!-- Arbre vide -->
                <div v-else-if="filteredNodes.length === 0 && (viewMode === 'tree' || searchQuery.trim() || !canCreate)" class="text-center py-5 text-muted">
                    <i class="far fa-folder-open mb-3" style="font-size: 40px; color: #cbd5e1;"></i>
                    <p class="mb-0">Aucune famille d'articles trouvée.</p>
                </div>

                <!-- Vue en colonnes : résultats de recherche -->
                <div v-else-if="viewMode === 'columns' && searchQuery.trim()" class="family-columns-search">
                    <div v-for="node in searchResults"
                         :key="node.id"
                         class="family-col-row"
                         @click="selectFromSearch(node)">
                        <i class="far fa-folder col-row-icon mr-3"></i>
                        <span class="col-row-name">{{ node.full_path || node.name }}</span>
                        <span class="col-row-count ml-auto">{{ totalArticles[node.id] || 0 }}</span>
                    </div>
                </div>

                <!-- Vue en colonnes (familles / sous-familles) -->
                <div v-else-if="viewMode === 'columns'" class="family-columns" ref="columns">
                    <div v-for="(column, colIndex) in columns"
                         :key="'col-' + (column.parentId || 'root')"
                         class="family-column"
                         :class="{ 'is-drop-column': dropColumnKey === (column.parentId || 'root') }"
                         @dragover="onColumnDragOver($event, column)"
                         @dragleave="onColumnDragLeave($event)"
                         @drop="onColumnDrop($event, column)">
                        <div v-for="node in column.nodes"
                             :key="node.id"
                             class="family-col-row"
                             :class="{
                                'is-selected': selectedPath[colIndex] === node.id,
                                'is-empty': !totalArticles[node.id],
                                'is-dragging': draggedNodeId === node.id,
                                'is-drop-inside': dropTargetId === node.id && dropPosition === 'inside',
                                'is-drop-before': dropTargetId === node.id && dropPosition === 'before',
                                'is-drop-after': dropTargetId === node.id && dropPosition === 'after'
                             }"
                             :draggable="canEdit"
                             @dragstart="onDragStart($event, node)"
                             @dragend="onDragEnd"
                             @dragover.stop="onColumnRowDragOver($event, colIndex, node)"
                             @drop.stop="onColumnRowDrop($event, node)"
                             @click="selectColumnNode($event, colIndex, node)">
                            <i class="far col-row-icon mr-3" :class="selectedPath[colIndex] === node.id ? 'fa-folder-open' : 'fa-folder'"></i>
                            <span class="col-row-nomenclature mr-2">{{ node.nomenclature || node.nomenclature_auto }}</span>
                            <span class="col-row-name">{{ node.name }}</span>

                            <div class="col-row-actions ml-auto">
                                <a class="col-row-action" :href="'/stocks/article-families/' + node.id" title="Consulter">
                                    <i class="far fa-search"></i>
                                </a>
                                <button v-if="canEdit" type="button" class="col-row-action" title="Modifier" @click="openEditModal(node)">
                                    <i class="far fa-pen"></i>
                                </button>
                                <button v-if="canDelete && !nodeHasArticles(node)" type="button" class="col-row-action danger" title="Supprimer" @click="deleteFamily(node)">
                                    <i class="far fa-trash"></i>
                                </button>
                            </div>

                            <span class="col-row-count ml-2" :title="'Articles (sous-familles incluses)'">{{ totalArticles[node.id] || 0 }}</span>
                            <span class="col-row-chevron ml-2">
                                <i v-if="node.children.length > 0" class="far fa-chevron-right"></i>
                            </span>
                        </div>

                        <!-- Création inline en bas de colonne -->
                        <template v-if="canCreate">
                            <div v-if="columnCreateKey === (column.parentId || 'root')" class="family-col-create" @click.stop>
                                <input type="text"
                                       class="form-control form-control-sm inline-create-nomenclature mr-2"
                                       v-model="inlineCreateNomenclature"
                                       :disabled="inlineSubmitting"
                                       placeholder="N°"
                                       title="Nomenclature"
                                       @keyup.enter="submitInlineCreate"
                                       @keyup.esc="cancelInlineCreate"
                                       style="width: 70px;">
                                <input type="text"
                                       class="form-control form-control-sm inline-create-input column-create-input"
                                       v-model="inlineCreateName"
                                       :disabled="inlineSubmitting"
                                       :placeholder="column.parentId ? 'Nom de la sous-famille...' : 'Nom de la famille...'"
                                       @keyup.enter="submitInlineCreate"
                                       @keyup.esc="cancelInlineCreate">
                                <button class="ix-btn green ml-2" @click="submitInlineCreate" :disabled="inlineSubmitting || !inlineCreateName.trim()">
                                    <i class="far" :class="inlineSubmitting ? 'fa-circle-notch fa-spin' : 'fa-check'"></i>
                                </button>
                                <button class="ix-btn transparent ml-1" @click="cancelInlineCreate" :disabled="inlineSubmitting" title="Annuler">
                                    <i class="far fa-times"></i>
                                </button>
                            </div>
                            <div v-else class="family-col-row is-new" @click="startColumnCreate(column.parentId)">
                                <i class="far fa-plus col-row-icon mr-3"></i>
                                <span>{{ column.parentId ? 'Nouvelle sous-famille' : 'Nouvelle famille' }}</span>
                            </div>
                        </template>

                        <div v-if="column.nodes.length === 0 && !canCreate" class="text-muted small px-3 py-2">
                            Aucune sous-famille.
                        </div>
                    </div>
                </div>

                <!-- Liste d'arborescence sous forme de nœuds plats calculés -->
                <div v-else class="family-tree-container">
                    <transition-group name="tree-list" tag="div" class="family-tree-list">
                        <div v-for="node in filteredNodes" 
                             :key="node.id" 
                             class="family-node-row d-flex flex-column rounded mb-1"
                             :class="[
                                'depth-' + Math.min(node.depth, 3),
                                { 'is-dragging': draggedNodeId === node.id, 'is-drop-target': dropTargetId === node.id, 'is-drop-before': dropPosition === 'before' && dropTargetId === node.id, 'is-drop-after': dropPosition === 'after' && dropTargetId === node.id }
                             ]"
                             :draggable="canEdit && inlineCreateParentId !== node.id"
                             @dragstart="onDragStart($event, node)"
                             @dragend="onDragEnd"
                             @dragover="onDragOver($event, node)"
                             @dragleave="onDragLeave"
                             @drop="onDrop($event, node)">
                            
                            <!-- Ligne du nœud : Barre couleur + Chevron + Dossier + Nom + Badge + Actions -->
                            <div class="node-main-row d-flex align-items-center justify-content-between py-2 w-100"
                                 @click="onNodeClick($event, node)">
                                <!-- Partie Gauche -->
                                <div class="d-flex align-items-center align-self-stretch">
                                    <!-- Indentation par padding -->
                                    <div :style="{ paddingLeft: (node.depth * 20) + 'px' }" class="d-flex align-items-center">
                                        <!-- Chevron pour déplier/replier -->
                                        <span class="chevron-area mr-1 d-inline-flex align-items-center justify-content-center"
                                              :class="{ 'has-children': node.children && node.children.length > 0 }">
                                            <i v-if="node.children && node.children.length > 0"
                                               class="far fa-chevron-right text-muted chevron-icon"
                                               :style="{ transform: isExpanded(node.id) ? 'rotate(90deg)' : 'none' }"></i>
                                        </span>

                                        <!-- Icône dossier -->
                                        <i class="far mr-2 node-folder-icon"
                                           :class="node.children && node.children.length > 0 ? (isExpanded(node.id) ? 'fa-folder-open' : 'fa-folder') : 'fa-folder'"></i>

                                        <!-- Nomenclature -->
                                        <span class="node-nomenclature mr-2" :class="'node-nomenclature-depth-' + Math.min(node.depth, 2)">
                                            {{ node.nomenclature || node.nomenclature_auto }}
                                        </span>

                                        <!-- Nom de la famille -->
                                        <span :class="['node-name', 'node-depth-' + Math.min(node.depth, 2)]">
                                            {{ node.name }}
                                        </span>

                                        <!-- Badge nombre d'enfants -->
                                        <span v-if="node.children && node.children.length > 0" class="child-count-badge ml-2">
                                            {{ node.children.length }}
                                        </span>

                                        <!-- Menu d'actions -->
                                        <div class="node-actions-group">
                                            <div class="btn-group">
                                                <button type="button" class="ix-btn-dropdown-actions" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" data-boundary="viewport">
                                                    <i class="far fa-ellipsis-h"></i>
                                                </button>
                                                <div class="dropdown-menu dropdown-menu-right">
                                                    <a class="ix-btn-dropdown-item" :href="'/stocks/article-families/' + node.id">
                                                        <i class="far fa-search"></i> Consulter
                                                    </a>
                                                    <button v-if="canEdit" class="ix-btn-dropdown-item" @click="openEditModal(node)">
                                                        <i class="far fa-pen"></i> Modifier
                                                    </button>
                                                    <button v-if="canDelete && !nodeHasArticles(node)" class="ix-btn-dropdown-item danger" @click="deleteFamily(node)">
                                                        <i class="far fa-trash"></i> Supprimer
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>

                            <!-- Ligne de création inline de sous-famille (en dessous du nœud) -->
                            <div v-if="inlineCreateParentId === node.id"
                                 class="inline-create-row d-flex align-items-center py-2 px-3 ml-2 mb-1 rounded">
                                <div :style="{ paddingLeft: ((node.depth + 1) * 20) + 'px' }" class="d-flex align-items-center w-100">
                                    <i class="far fa-folder-plus text-primary mr-2" style="font-size: 13px;"></i>
                                    <input type="text"
                                           class="form-control form-control-sm inline-create-input"
                                           v-model="inlineCreateName"
                                           :disabled="inlineSubmitting"
                                           placeholder="Nom (ou A > B > C pour une hiérarchie)..."
                                           @keyup.enter="submitInlineCreate"
                                           @keyup.esc="cancelInlineCreate"
                                           @click.stop
                                           style="max-width: 300px;">
                                    <input type="text"
                                           class="form-control form-control-sm inline-create-nomenclature ml-2"
                                           v-model="inlineCreateNomenclature"
                                           :disabled="inlineSubmitting"
                                           placeholder="N°"
                                           @keyup.enter="submitInlineCreate"
                                           @keyup.esc="cancelInlineCreate"
                                           @click.stop
                                           style="max-width: 80px;"
                                           title="Nomenclature">
                                    <button class="ix-btn green ml-2" @click.stop="submitInlineCreate" :disabled="inlineSubmitting || !inlineCreateName.trim()">
                                        <i class="far fa-check mr-1"></i> Créer
                                    </button>
                                    <button class="ix-btn transparent ml-1" @click.stop="cancelInlineCreate" :disabled="inlineSubmitting">
                                        Annuler
                                    </button>
                                    <span v-if="inlineSubmitting" class="ml-2 text-muted small"><i class="far fa-circle-notch fa-spin"></i></span>
                                </div>
                            </div>
                        </div>
                    </transition-group>
                </div>
            </div>
        </div>

        <!-- Boîte modale de Modification d'une famille uniquement -->
        <div class="modal fade" id="familyFormModal" tabindex="-1" role="dialog" aria-hidden="true" style="z-index: 1060;">
            <div class="modal-dialog modal-lg" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            <i class="far fa-folder mr-2"></i>
                            {{ isEditing ? 'Modifier la famille' : 'Ajouter une famille d\'articles' }}
                        </h5>
                        <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="closeModal">
                            <i class="fal fa-times"></i>
                        </button>
                    </div>
                    
                    <div class="modal-body">
                        <!-- Message d'erreur -->
                        <div v-if="modalError" class="alert alert-danger mb-3 d-flex align-items-center">
                            <i class="far fa-exclamation-circle mr-2 h5 mb-0"></i> {{ modalError }}
                        </div>

                        <!-- Mode édition : champ nom simple -->
                        <div class="row">
                            <div class="col-sm-8 mb-3">
                                <label for="family_name" class="required">Nom</label>
                                <input id="family_name" type="text" class="form-control" v-model="form.name" required>
                            </div>

                            <div class="col-sm-4 mb-3">
                                <label for="family_nomenclature">Nomenclature</label>
                                <input id="family_nomenclature" type="text" class="form-control" v-model="form.nomenclature"
                                       placeholder="Ex : 1, A, 1.1...">
                            </div>

                            <div v-if="isEditing" class="col-sm-12 mb-3">
                                <label for="parent_id">Parent</label>
                                <select id="parent_id" class="form-control" v-model="form.parent_id">
                                    <option :value="null">[ Aucun ]</option>
                                    <option v-for="parent in parentOptions" 
                                            :key="parent.id" 
                                            :value="parent.id"
                                            :disabled="parent.id === editingId || isDescendantOf(parent.id, editingId)">
                                        {{ parent.full_path || parent.name }}
                                    </option>
                                </select>
                            </div>

                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" class="ix-btn green" @click="submitForm" :disabled="submitting || !form.name">
                            <span v-if="submitting"><i class="far fa-circle-notch fa-spin mr-2"></i></span>
                            <span>Enregistrer</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import $ from "jquery";

export default {
    name: 'article-family-tree-manager',
    props: {
        canCreate: {
            type: Boolean,
            default: false
        },
        canEdit: {
            type: Boolean,
            default: false
        },
        canDelete: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            families: [],
            loading: true,
            error: null,
            searchQuery: '',
            hoveredNodeId: null,
            expandedNodeIds: new Set(),
            allExpanded: false,
            
            // État de la modale
            isEditing: false,
            editingId: null,
            submitting: false,
            modalError: '',
            form: {
                name: '',
                parent_id: null,
                nomenclature: ''
            },

            // État de la création inline
            inlineCreateParentId: null,
            inlineCreateName: '',
            inlineCreateNomenclature: '',
            inlineSubmitting: false,
            nomenclatureRequestId: 0,

            // Vue en colonnes
            viewMode: 'columns',
            selectedPath: [],
            columnCreateKey: null,
            dropColumnKey: null,
            hoverOpenTimer: null,

            // État du drag and drop
            draggedNodeId: null,
            dropTargetId: null,
            dropPosition: null
        };
    },
    computed: {
        // Renvoie l'arborescence plate ordonnée
        flatTree() {
            const map = {};
            this.families.forEach(fam => {
                map[fam.id] = { ...fam, children: [] };
            });

            const roots = [];
            this.families.forEach(fam => {
                const node = map[fam.id];
                if (node.parent_id && map[node.parent_id]) {
                    map[node.parent_id].children.push(node);
                } else {
                    roots.push(node);
                }
            });

            const flat = [];
            const traverse = (node, depth, path, autoNomenclature) => {
                node.depth = depth;
                node.path = path;
                node.nomenclature_auto = autoNomenclature;
                flat.push(node);
                if (node.children && node.children.length > 0) {
                    node.children.forEach((child, index) => {
                        traverse(child, depth + 1, [...path, node.id], autoNomenclature + '.' + (index + 1));
                    });
                }
            };

            roots.forEach((root, index) => {
                traverse(root, 0, [], String(index + 1));
            });

            return flat;
        },
        
        // Filtre les nœuds selon l'état de dépliage et de recherche
        filteredNodes() {
            const query = this.searchQuery.trim().toLowerCase();
            
            if (!query) {
                // Sans recherche, on n'affiche que les nœuds dont tous les ancêtres sont dépliés
                return this.flatTree.filter(node => {
                    return node.path.every(parentId => this.expandedNodeIds.has(parentId));
                });
            }
            
            const matchingNodeIds = new Set();
            this.flatTree.forEach(node => {
                if (node.name.toLowerCase().includes(query)) {
                    matchingNodeIds.add(node.id);
                }
            });

            const nodesToShow = new Set();
            this.flatTree.forEach(node => {
                const hasMatchingAncestor = node.path.some(parentId => matchingNodeIds.has(parentId));
                const isAncestorOfMatch = this.flatTree.some(other => 
                    matchingNodeIds.has(other.id) && other.path.includes(node.id)
                );

                if (matchingNodeIds.has(node.id) || hasMatchingAncestor || isAncestorOfMatch) {
                    nodesToShow.add(node.id);
                    if (isAncestorOfMatch) {
                        this.expandedNodeIds.add(node.id);
                    }
                }
            });

            return this.flatTree.filter(node => nodesToShow.has(node.id));
        },

        nodeById() {
            const map = {};
            this.flatTree.forEach(node => { map[node.id] = node; });
            return map;
        },

        // Nombre d'articles par famille, sous-familles incluses
        totalArticles() {
            const totals = {};
            const count = node => {
                totals[node.id] = (node.articles_count || 0)
                    + node.children.reduce((sum, child) => sum + count(child), 0);
                return totals[node.id];
            };
            this.flatTree.filter(node => node.depth === 0).forEach(count);
            return totals;
        },

        // Colonnes affichées : racines, puis les enfants de chaque famille sélectionnée
        columns() {
            const cols = [{ parentId: null, nodes: this.flatTree.filter(node => node.depth === 0) }];
            this.selectedPath.forEach(id => {
                const node = this.nodeById[id];
                if (node && (node.children.length > 0 || this.canCreate)) {
                    cols.push({ parentId: id, nodes: node.children });
                }
            });
            return cols;
        },

        searchResults() {
            const query = this.searchQuery.trim().toLowerCase();
            return this.flatTree.filter(node => node.name.toLowerCase().includes(query));
        },

        // Options éligibles pour la famille parente
        parentOptions() {
            return this.families.map(f => ({
                id: f.id,
                name: f.name,
                full_path: f.full_path,
                parent_id: f.parent_id
            })).sort((a, b) => a.name.localeCompare(b.name));
        }
    },
    methods: {
        setViewMode(mode) {
            this.viewMode = mode;
            this.cancelInlineCreate();
            try { localStorage.setItem('articleFamilies.viewMode', mode); } catch (e) {}
        },

        selectColumnNode(event, colIndex, node) {
            if (event.target.closest('.col-row-actions')) return;
            this.cancelInlineCreate();
            this.selectedPath = [...this.selectedPath.slice(0, colIndex), node.id];
            this.scrollColumnsToEnd();
        },

        selectFromSearch(node) {
            this.searchQuery = '';
            this.selectedPath = [...node.path, node.id];
            this.scrollColumnsToEnd();
        },

        startColumnCreate(parentId) {
            this.cancelInlineCreate();
            this.columnCreateKey = parentId || 'root';
            this.inlineCreateParentId = parentId;
            // Nomenclature laissée vide à la création
            // this.fetchNextNomenclature(parentId, 'inline');
            this.$nextTick(() => {
                const input = this.$el.querySelector('.column-create-input');
                if (input) input.focus();
            });
        },

        scrollColumnsToEnd() {
            this.$nextTick(() => {
                const el = this.$refs.columns;
                if (el) el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
            });
        },

        nodeHasArticles(node) {
            if (node.articles_count > 0) return true;
            if (node.children && node.children.length > 0) {
                return node.children.some(child => this.nodeHasArticles(child));
            }
            return false;
        },

        onNodeClick(event, node) {
            if (event.target.closest('.node-actions-group') || event.target.closest('.inline-create-row') || event.target.closest('.btn-group') || event.target.closest('.dropdown-menu')) return;
            if (this.inlineCreateParentId === node.id) return;
            if (node.children && node.children.length > 0) {
                this.toggleNode(node.id);
            }
            if (this.canCreate) {
                this.startInlineCreate(node.id);
            }
        },

        // --- Drag and Drop ---
        onDragStart(event, node) {
            this.draggedNodeId = node.id;
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', String(node.id));
        },

        onDragEnd() {
            clearTimeout(this.hoverOpenTimer);
            this.dropColumnKey = null;
            this.draggedNodeId = null;
            this.dropTargetId = null;
            this.dropPosition = null;
        },

        onDragOver(event, node) {
            if (!this.draggedNodeId || this.draggedNodeId === node.id) return;
            if (this.isDescendantOf(node.id, this.draggedNodeId)) return;

            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';

            const rect = event.currentTarget.getBoundingClientRect();
            const offsetY = event.clientY - rect.top;
            const h = rect.height;

            if (offsetY < h * 0.3) {
                this.dropPosition = 'before';
            } else if (offsetY > h * 0.7) {
                this.dropPosition = 'after';
            } else {
                this.dropPosition = 'inside';
            }
            this.dropTargetId = node.id;
        },

        onDragLeave() {
            this.dropTargetId = null;
            this.dropPosition = null;
        },

        async onDrop(event, node) {
            event.preventDefault();
            const draggedId = this.draggedNodeId;
            const targetId = node.id;
            const position = this.dropPosition;

            this.draggedNodeId = null;
            this.dropTargetId = null;
            this.dropPosition = null;

            if (!draggedId || draggedId === targetId) return;
            if (this.isDescendantOf(targetId, draggedId)) return;

            if (position === 'inside') {
                this.expandedNodeIds.add(targetId);
                this.expandedNodeIds = new Set(this.expandedNodeIds);
            }
            await this.dropOnNode(draggedId, node, position);
        },

        // --- Drag and Drop (vue en colonnes) ---
        canDropInto(parentId) {
            if (!this.draggedNodeId) return false;
            if (parentId === null) return true;
            return parentId !== this.draggedNodeId && !this.isDescendantOf(parentId, this.draggedNodeId);
        },

        onColumnRowDragOver(event, colIndex, node) {
            if (!this.draggedNodeId) return;
            this.dropColumnKey = null;

            if (node.id === this.draggedNodeId) {
                // Survol de soi-même : le drop est autorisé mais sans effet
                event.preventDefault();
                this.clearDropTarget();
                return;
            }
            if (!this.canDropInto(node.parent_id || null)) return;

            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';

            const rect = event.currentTarget.getBoundingClientRect();
            const offsetY = event.clientY - rect.top;
            let position;
            if (offsetY < rect.height * 0.3) {
                position = 'before';
            } else if (offsetY > rect.height * 0.7) {
                position = 'after';
            } else {
                position = this.canDropInto(node.id) ? 'inside' : (offsetY < rect.height / 2 ? 'before' : 'after');
            }

            // Survol prolongé au centre : ouvre la famille pour pouvoir déposer plus profond
            if (position === 'inside' && (this.dropTargetId !== node.id || this.dropPosition !== 'inside')) {
                clearTimeout(this.hoverOpenTimer);
                this.hoverOpenTimer = setTimeout(() => {
                    if (this.draggedNodeId && this.dropTargetId === node.id && this.dropPosition === 'inside') {
                        this.selectedPath = [...this.selectedPath.slice(0, colIndex), node.id];
                        this.scrollColumnsToEnd();
                    }
                }, 700);
            }

            this.dropTargetId = node.id;
            this.dropPosition = position;
        },

        onColumnRowDrop(event, node) {
            event.preventDefault();
            const draggedId = this.draggedNodeId;
            const position = this.dropPosition;
            const targetId = this.dropTargetId;
            this.onDragEnd();
            if (!draggedId || targetId !== node.id || !position) return;
            this.dropOnNode(draggedId, node, position);
        },

        // Survol de l'espace vide d'une colonne : ajout en fin de colonne
        onColumnDragOver(event, column) {
            if (!this.canDropInto(column.parentId)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
            this.clearDropTarget();
            this.dropColumnKey = column.parentId || 'root';
        },

        onColumnDragLeave(event) {
            if (!event.currentTarget.contains(event.relatedTarget)) {
                this.dropColumnKey = null;
            }
        },

        onColumnDrop(event, column) {
            event.preventDefault();
            const draggedId = this.draggedNodeId;
            this.onDragEnd();
            if (!draggedId || !this.canDropIntoFor(draggedId, column.parentId)) return;
            const siblings = column.nodes.map(n => n.id).filter(id => id !== draggedId);
            this.moveFamily(draggedId, column.parentId, [...siblings, draggedId]);
        },

        canDropIntoFor(draggedId, parentId) {
            if (parentId === null) return true;
            return parentId !== draggedId && !this.isDescendantOf(parentId, draggedId);
        },

        clearDropTarget() {
            clearTimeout(this.hoverOpenTimer);
            this.dropTargetId = null;
            this.dropPosition = null;
        },

        // Calcule le nouvel ordre des frères selon la position de dépôt
        dropOnNode(draggedId, targetNode, position) {
            if (draggedId === targetNode.id) return;

            if (position === 'inside') {
                const siblings = targetNode.children.map(n => n.id).filter(id => id !== draggedId);
                return this.moveFamily(draggedId, targetNode.id, [...siblings, draggedId]);
            }

            const parentId = targetNode.parent_id || null;
            const siblings = this.flatTree
                .filter(n => (n.parent_id || null) === parentId && n.id !== draggedId)
                .map(n => n.id);
            let index = siblings.indexOf(targetNode.id);
            if (position === 'after') index++;
            siblings.splice(index, 0, draggedId);
            return this.moveFamily(draggedId, parentId, siblings);
        },

        async moveFamily(draggedId, parentId, orderedIds) {
            const dragged = this.families.find(f => f.id === draggedId);
            if (!dragged) return;

            const unchanged = (dragged.parent_id || null) === parentId
                && this.flatTree.filter(n => (n.parent_id || null) === parentId).map(n => n.id).join(',') === orderedIds.join(',');
            if (unchanged) return;

            // Mise à jour optimiste pour éviter le rechargement complet
            this.families = this.families
                .map(f => {
                    const index = orderedIds.indexOf(f.id);
                    if (index === -1) return f;
                    return { ...f, parent_id: f.id === draggedId ? parentId : f.parent_id, position: index + 1 };
                })
                .sort((a, b) => (a.position || 0) - (b.position || 0) || a.name.localeCompare(b.name));
            this.pruneSelection();

            const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
            try {
                const response = await fetch('/stocks/article-families/reorder', {
                    method: 'POST',
                    body: JSON.stringify({ parent_id: parentId, ids: orderedIds }),
                    ...this.getHttpJsonRequestHeaders(),
                    headers: {
                        ...this.getHttpJsonRequestHeaders().headers,
                        'X-CSRF-TOKEN': csrfToken
                    }
                });
                const data = await response.json();
                if (!response.ok || !data.success) throw new Error(data.message || "Erreur lors du déplacement.");
                if (window.toastr) window.toastr.success(data.message);
                this.fetchFamilies(true);
            } catch (error) {
                if (window.toastr) window.toastr.error(error.message);
                this.fetchFamilies(true);
            }
        },

        // Retire de la sélection les familles supprimées ou déplacées
        pruneSelection() {
            const valid = [];
            for (const id of this.selectedPath) {
                const fam = this.families.find(f => f.id === id);
                if (!fam || (fam.parent_id || null) !== (valid.length ? valid[valid.length - 1] : null)) break;
                valid.push(id);
            }
            this.selectedPath = valid;
        },

        openImportModal() {
            $('#importFamiliesModal').modal('show');
        },
        fetchFamilies(silent = false) {
            this.loading = !silent;
            this.error = null;
            
            fetch('/stocks/article-families/tree', {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            })
            .then(response => {
                if (!response.ok) throw new Error("Impossible de charger les familles d'articles.");
                return response.json();
            })
            .then(data => {
                if (data.success) {
                    this.families = data.families;
                    this.pruneSelection();
                } else {
                    this.error = "Erreur de format de réponse.";
                }
                this.loading = false;
            })
            .catch(error => {
                this.error = error.message || "Une erreur est survenue.";
                this.loading = false;
            });
        },

        expandFirstLevel() {
            this.families.forEach(f => {
                if (!f.parent_id) {
                    this.expandedNodeIds.add(f.id);
                }
            });
        },

        isExpanded(id) {
            return this.expandedNodeIds.has(id);
        },

        toggleNode(id) {
            if (this.expandedNodeIds.has(id)) {
                this.expandedNodeIds.delete(id);
            } else {
                this.expandedNodeIds.add(id);
            }
            this.expandedNodeIds = new Set(this.expandedNodeIds);
        },

        toggleAllNodes() {
            if (this.allExpanded) {
                this.expandedNodeIds = new Set();
                this.allExpanded = false;
            } else {
                const withChildrenIds = this.families
                    .filter(f => this.families.some(child => child.parent_id === f.id))
                    .map(f => f.id);
                this.expandedNodeIds = new Set(withChildrenIds);
                this.allExpanded = true;
            }
        },

        isDescendantOf(candidateParentId, currentId) {
            let current = this.families.find(f => f.id === candidateParentId);
            while (current) {
                if (current.parent_id === currentId) return true;
                current = this.families.find(f => f.id === current.parent_id);
            }
            return false;
        },

        // --- Création inline de sous-famille ---
        startInlineCreate(parentId) {
            this.cancelInlineCreate();
            this.inlineCreateParentId = parentId;
            this.inlineCreateName = '';
            this.inlineCreateNomenclature = '';
            this.expandedNodeIds.add(parentId);
            this.expandedNodeIds = new Set(this.expandedNodeIds);
            // Nomenclature laissée vide à la création
            // this.fetchNextNomenclature(parentId, 'inline');
            this.$nextTick(() => {
                const input = this.$el.querySelector('.inline-create-input');
                if (input) input.focus();
            });
        },

        cancelInlineCreate() {
            this.columnCreateKey = null;
            this.inlineCreateParentId = null;
            this.inlineCreateName = '';
            this.inlineCreateNomenclature = '';
            this.inlineSubmitting = false;
        },

        async submitInlineCreate() {
            if (!this.inlineCreateName.trim() || this.inlineSubmitting) return;
            this.inlineSubmitting = true;

            const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

            try {
                const response = await fetch('/stocks/article-families', {
                    method: 'POST',
                    body: JSON.stringify({
                        name: this.inlineCreateName.trim(),
                        parent_id: this.inlineCreateParentId,
                        nomenclature: this.inlineCreateNomenclature.trim()
                    }),
                    ...this.getHttpJsonRequestHeaders(),
                    headers: {
                        ...this.getHttpJsonRequestHeaders().headers,
                        'X-CSRF-TOKEN': csrfToken
                    }
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.message || "Erreur lors de la création.");
                if (data.success) {
                    if (window.toastr) window.toastr.success(data.message);
                    this.inlineCreateName = '';
                    this.inlineCreateParentId = null;
                    this.columnCreateKey = null;
                    await this.fetchFamilies(this.viewMode === 'columns');
                    this.expandedNodeIds.add(data.family.parent_id);
                    this.expandedNodeIds = new Set(this.expandedNodeIds);
                }
            } catch (error) {
                if (window.toastr) window.toastr.error(error.message);
            } finally {
                this.inlineSubmitting = false;
            }
        },

        // --- Boîtes Modales de gestion ---
        openCreateModal(parentId = null) {
            this.isEditing = false;
            this.editingId = null;
            this.modalError = '';
            this.form.name = '';
            this.form.parent_id = parentId;
            this.form.nomenclature = '';
            // Nomenclature laissée vide à la création
            // this.fetchNextNomenclature(parentId);
            
            $("#familyFormModal").modal("show");
        },

        openEditModal(node) {
            this.nomenclatureRequestId++;
            this.isEditing = true;
            this.editingId = node.id;
            this.modalError = '';
            this.form.name = node.name;
            this.form.parent_id = node.parent_id;
            this.form.nomenclature = node.nomenclature || '';
            
            $("#familyFormModal").modal("show");
        },

        closeModal() {
            $("#familyFormModal").modal("hide");
        },

        fetchNextNomenclature(parentId, target = 'form') {
            const requestId = ++this.nomenclatureRequestId;
            const url = parentId
                ? '/stocks/article-families/next-nomenclature?parent_id=' + parentId
                : '/stocks/article-families/next-nomenclature';

            fetch(url, {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            })
            .then(response => response.json())
            .then(data => {
                if (!data.success || requestId !== this.nomenclatureRequestId) return;
                if (target === 'inline') {
                    if (!this.inlineCreateNomenclature) {
                        this.inlineCreateNomenclature = data.nomenclature;
                    }
                } else if (!this.isEditing && !this.form.nomenclature) {
                    this.form.nomenclature = data.nomenclature;
                }
            })
            .catch(() => {});
        },

        submitForm() {
            if (!this.form.name) return;
            
            this.submitting = true;
            this.modalError = '';
            
            const url = this.isEditing ? `/stocks/article-families/${this.editingId}` : '/stocks/article-families';
            const method = this.isEditing ? 'PUT' : 'POST';
            
            const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
            
            fetch(url, {
                method: method,
                body: JSON.stringify(this.form),
                ...this.getHttpJsonRequestHeaders(),
                headers: {
                    ...this.getHttpJsonRequestHeaders().headers,
                    'X-CSRF-TOKEN': csrfToken
                }
            })
            .then(async response => {
                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data.message || "Erreur lors de l'enregistrement.");
                }
                return data;
            })
            .then(data => {
                if (data.success) {
                    if (window.toastr) {
                        window.toastr.success(data.message);
                    }
                    this.closeModal();
                    this.fetchFamilies(true);
                }
                this.submitting = false;
            })
            .catch(error => {
                this.modalError = error.message;
                this.submitting = false;
            });
        },

        deleteFamily(node) {
            this.$swal({
                title: "Supprimer la famille ?",
                text: `Êtes-vous sûr de vouloir supprimer "${node.name}" ? Les sous-familles associées seront rattachées à son parent. La suppression est impossible si des articles y sont rattachés.`,
                icon: "warning",
                buttons: ["Annuler", "Supprimer"],
                dangerMode: true
            }).then((confirm) => {
                if (confirm) {
                    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
                    
                    fetch(`/stocks/article-families/${node.id}`, {
                        method: 'DELETE',
                        ...this.getHttpJsonRequestHeaders(),
                        headers: {
                            ...this.getHttpJsonRequestHeaders().headers,
                            'X-CSRF-TOKEN': csrfToken
                        }
                    })
                    .then(async response => {
                        const data = await response.json();
                        if (!response.ok) {
                            throw new Error(data.message || "Erreur lors de la suppression.");
                        }
                        return data;
                    })
                    .then(data => {
                        if (data.success) {
                            if (window.toastr) {
                                window.toastr.success(data.message);
                            }
                            this.fetchFamilies(true);
                        }
                    })
                    .catch(error => {
                        this.$swal({
                            title: "Erreur",
                            text: error.message,
                            icon: "error"
                        });
                    });
                }
            });
        }
    },
    mounted() {
        // Vue arborescente désactivée : on force la vue en colonnes
        // try {
        //     const mode = localStorage.getItem('articleFamilies.viewMode');
        //     if (mode === 'tree' || mode === 'columns') this.viewMode = mode;
        // } catch (e) {}
        this.fetchFamilies();
    }
};
</script>

<style scoped>
.family-tree-list {
    user-select: none;
}

.family-node-row {
    transition: background-color 0.15s ease, box-shadow 0.15s ease;
    border-radius: 8px !important;
    border: 1px solid transparent;
    background: #fff;
}

.family-node-row:hover {
    background-color: #f1f5f9 !important;
}

.family-node-row[draggable="true"] {
    cursor: grab;
}
.family-node-row[draggable="true"]:active {
    cursor: grabbing;
}
.family-node-row.is-dragging {
    cursor: grabbing !important;
}

/* Zone chevron */
.chevron-area {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
}
.chevron-area.has-children {
    cursor: pointer;
}
.chevron-icon {
    transition: transform 0.2s ease;
    font-size: 12px;
}

/* Icône dossier */
.node-folder-icon {
    font-size: 15px;
    color: #64748b;
}
.depth-0 .node-folder-icon { color: #3b82f6; }
.depth-1 .node-folder-icon { color: #8b5cf6; }
.depth-2 .node-folder-icon { color: #ec4899; }

/* Nom */
.node-name {
    font-size: 13.5px;
    color: #334155;
    line-height: 1.4;
}
.node-name.node-depth-0 {
    font-weight: 700;
    font-size: 14.5px;
    color: #1e293b;
}
.node-name.node-depth-1 {
    font-weight: 600;
}

/* Nomenclature */
.node-nomenclature {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #94a3b8;
}
.node-nomenclature-depth-0 {
    font-weight: 700;
    font-size: 13px;
    color: #475569;
}
.node-nomenclature-depth-1 {
    font-weight: 600;
    color: #64748b;
}

/* Badge nombre d'enfants */
.child-count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background-color: #e2e8f0;
    color: #64748b;
    font-size: 11px;
    font-weight: 600;
}

/* Actions - visibles au survol de la ligne ou quand le dropdown est ouvert */
.node-actions-group {
    opacity: 0;
    transition: opacity 0.15s ease-in-out;
    display: inline-flex;
    align-items: center;
    margin-left: 6px;
}
.family-node-row:hover .node-actions-group,
.node-actions-group .btn-group.show {
    opacity: 1;
}

/* Drag and drop visuels */
.family-node-row.is-dragging {
    opacity: 0.4;
}
.family-node-row.is-drop-target {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}
.family-node-row.is-drop-before {
    border-top: 3px solid #3b82f6;
}
.family-node-row.is-drop-after {
    border-bottom: 3px solid #3b82f6;
}

/* Transitions de liste */
.tree-list-enter-active, .tree-list-leave-active {
    transition: all 0.25s ease;
}
.tree-list-enter, .tree-list-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

/* Ligne de création inline */
.inline-create-row {
    animation: slideDown 0.2s ease;
    background-color: #f0f7ff;
    border: 1px solid #bfdbfe;
    margin-left: 4px;
}
@keyframes slideDown {
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
}
.inline-create-input {
    border: 1px solid #93c5fd !important;
    border-radius: 6px !important;
    font-size: 13.5px !important;
    height: 34px;
}
.inline-create-input:focus {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15) !important;
}
.inline-create-nomenclature {
    border: 1px solid #93c5fd !important;
    border-radius: 6px !important;
    font-size: 13px !important;
    height: 34px;
    text-align: center;
    font-family: 'Courier New', monospace;
}
.inline-create-nomenclature:focus {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15) !important;
}

/* Actions au survol (vue colonnes) */
.col-row-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #64748b;
    font-size: 12px;
    cursor: pointer;
}
.col-row-action:hover {
    background-color: #fff;
    color: #1d4ed8;
    text-decoration: none;
}
.col-row-action.danger:hover {
    color: #dc2626;
}

/* Drag and drop (vue colonnes) */
.family-col-row[draggable="true"] {
    cursor: grab;
}
.family-col-row {
    border: 1px solid transparent;
}
.family-col-row.is-dragging {
    opacity: 0.4;
}
.family-col-row.is-drop-inside {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}
.family-col-row.is-drop-before {
    box-shadow: inset 0 3px 0 #3b82f6;
}
.family-col-row.is-drop-after {
    box-shadow: inset 0 -3px 0 #3b82f6;
}
.family-column.is-drop-column {
    background-color: #f8fbff;
    box-shadow: inset 0 0 0 2px rgba(59, 130, 246, 0.25);
}

/* Sélecteur de vue */
.view-mode-switch .ix-btn.active {
    background-color: #e8effa;
    color: #1d4ed8;
}

/* Vue en colonnes */
.family-columns {
    display: flex;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow-x: auto;
    min-height: 260px;
    user-select: none;
}
.family-column {
    flex: 1 0 320px;
    max-width: 480px;
    padding: 10px;
    border-right: 1px solid #e2e8f0;
}
.family-column:last-child {
    border-right: 0;
}
.family-columns-search {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 10px;
}
.family-col-row {
    display: flex;
    align-items: center;
    padding: 10px 14px;
    margin-bottom: 2px;
    border-radius: 8px;
    cursor: pointer;
    color: #1e293b;
    font-size: 14.5px;
    transition: background-color 0.15s ease;
}
.family-col-row:hover {
    background-color: #f1f5f9;
}
.family-col-row.is-selected {
    background-color: #e8effa;
    color: #1d4ed8;
}
.family-col-row.is-empty:not(.is-selected) {
    color: #94a3b8;
}
.family-col-row.is-new {
    color: #94a3b8;
    font-size: 13.5px;
}
.family-col-row.is-new:hover {
    color: #3b82f6;
}
.col-row-icon {
    width: 16px;
    text-align: center;
    color: #64748b;
}
.family-col-row.is-selected .col-row-icon {
    color: #1d4ed8;
}
.family-col-row.is-empty:not(.is-selected) .col-row-icon,
.family-col-row.is-new .col-row-icon {
    color: #cbd5e1;
}
.col-row-nomenclature {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #94a3b8;
}
.col-row-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.col-row-count {
    min-width: 30px;
    height: 24px;
    padding: 0 9px;
    border-radius: 12px;
    background-color: #f1f5f9;
    color: #475569;
    font-size: 12px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.family-col-row.is-selected .col-row-count {
    background-color: #fff;
    color: #1d4ed8;
}
.col-row-chevron {
    width: 14px;
    font-size: 12px;
    color: #94a3b8;
}
.family-col-row.is-selected .col-row-chevron {
    color: #1d4ed8;
}
.col-row-actions {
    opacity: 0;
    transition: opacity 0.15s ease-in-out;
}
.family-col-row:hover .col-row-actions,
.col-row-actions .btn-group.show {
    opacity: 1;
}
.family-col-create {
    display: flex;
    align-items: center;
    padding: 6px 8px;
    border-radius: 8px;
    background-color: #f0f7ff;
    border: 1px solid #bfdbfe;
}
</style>
